import { Modal, ModalBody, Link } from 'carbon-components-react';
import { segmentsTracking } from 'src/common/lib/tracker';
import { Progress } from '../Landing/ProgressBar';
import { Restart16 } from '@carbon/icons-react';
import messages from '../../../locales/messages';
import { EmployeeExperience } from './employeeExperience';
import { ExpertAssistant } from './expertAssistant';
import { IntelligenceDocProcessing } from './intelligenceDocProcessing';
import { EnterpriseSearch } from './enterpriseSearch';
import './steps.scss';
import { useEffect, useState } from 'react';
import { SomethingElse } from './somethingElse';
import { FinalStep } from './finalStep';
import { LearnMore } from './learnMore';
import { createUseTranslation } from 'src/common/hooks/widgets/useTranslation';
import { useQradarEDRSelector } from '../../../lib/redux/hooks';
import { InteractiveDemo } from './interactiveDemos';

type StepsProps = {
  handleLaunchModal: () => void;
  experienceProducts: () => void;
  customerQuote: () => void;
  integrations: () => void;
  wxaiTrial: () => void;
  contactSales: () => void;
  digitalLabor: () => void;
  wxOrchestrateTrial: () => void;
  openSteps: boolean;
  setOpenSteps: React.Dispatch<React.SetStateAction<boolean>>;
  optionSelected: number;
  setOptionSelected: React.Dispatch<React.SetStateAction<number>>;
  stepcompleted: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setDrawOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFormID: React.Dispatch<React.SetStateAction<string>>;
  pathSelected: string;
  setPathSelected: React.Dispatch<React.SetStateAction<string>>;
};

export const Steps = ({
  handleLaunchModal,
  experienceProducts,
  customerQuote,
  integrations,
  wxaiTrial,
  contactSales,
  digitalLabor,
  wxOrchestrateTrial,
  openSteps,
  setOpenSteps,
  optionSelected,
  setOptionSelected,
  stepcompleted,
  setStep,
  setDrawOpen,
  setFormID,
  pathSelected,
  setPathSelected,
}: StepsProps) => {
  const [complete, setComplete] = useState(false);
  const [learnMore, setLearnMore] = useState(false);
  const [interactiveDemo, setInteractiveDemo] = useState(false);
  const AEMMessages = useQradarEDRSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  const selectTitle = () => {
    switch (optionSelected) {
      case 1:
        return useTranslation('employee_experience_title');
      case 2:
        return useTranslation('expert_assist_title');
      case 3:
        return useTranslation('intelligent_document_processing_title');
      case 4:
        return useTranslation('enterprice_search_title');
      case 5:
        return useTranslation('something_else_title');
    }
  };

  const renderComponent = () => {
    if (complete && !learnMore && optionSelected !== 5 && !interactiveDemo) {
      return (
        <FinalStep
          experienceProducts={experienceProducts}
          contactSales={contactSales}
          setLearnMore={setLearnMore}
          setInteractiveDemo={setInteractiveDemo}
          optionSelected={optionSelected}
          pathSelected={pathSelected}
        />
      );
    }
    if (complete && learnMore && !interactiveDemo) {
      return (
        <LearnMore
          customerQuote={customerQuote}
          integrations={integrations}
          experienceProducts={experienceProducts}
          digitalLabor={digitalLabor}
        />
      );
    }
    if (complete && !learnMore && interactiveDemo) {
      return (
        <InteractiveDemo
          wxai_Trial={wxaiTrial}
          wxOrchestrateTrial={wxOrchestrateTrial}
        />
      );
    }
    switch (optionSelected) {
      case 1:
        return <EmployeeExperience setStep={setStep} />;
      case 2:
        return <ExpertAssistant setStep={setStep} />;
      case 3:
        return <IntelligenceDocProcessing setStep={setStep} />;
      case 4:
        return <EnterpriseSearch setStep={setStep} />;
      case 5:
        return (
          <SomethingElse
            setDrawOpen={setDrawOpen}
            setFormID={setFormID}
            contactSales={contactSales}
          />
        );
    }
  };

  useEffect(() => {
    if (stepcompleted === 2) {
      setComplete(true);
    } else {
      setComplete(false);
      if (stepcompleted == 6) {
        handleLaunchModal();
        setInteractiveDemo(false);
      }
    }
  }, [stepcompleted]);

  return (
    <>
      <Modal
        passiveModal={true}
        open={openSteps}
        size={'lg'}
        id="ai-sa-step"
        onRequestClose={() => {
          segmentsTracking('CTA Clicked', {
            pageTitle: 'AI Solutions Advisor',
            object: 'AI Solutions Advisor page',
            resultValue: 'CTA clicked',
            CTA: 'Second Form modal closed on the corner',
            location: 'Steps modal',
            action: `Steps modal closed`,
            field: '',
          });
          setOpenSteps(false);
          setLearnMore(false);
          setComplete(false);
          setInteractiveDemo(false);
        }}
      >
        <ModalBody hasForm className="ModalBody">
          <div className="bx--row">
            <div className="bx--col-lg-13 bx--col-md-6 bx--col-sm-4">
              <p className="stepHeading"> {selectTitle()}</p>
            </div>
            <div className="bx--col-lg-3 bx--col-md-2 bx--col-sm-4 col2">
              <Link
                role="button"
                className="bx-link-class stepRestart"
                id="start-over-id"
                rel="noreferrer"
                renderIcon={() => <Restart16 className="Image" />}
                onClick={() => {
                  setComplete(false);
                  setLearnMore(false);
                  setInteractiveDemo(false);
                  handleLaunchModal();
                  setFormID('');
                  setPathSelected('');
                  segmentsTracking('CTA Clicked', {
                    pageTitle: 'AI Solutions Advisor',
                    object: 'AI Solutions Advisor page',
                    resultValue: 'CTA clicked',
                    CTA: 'Start Over',
                    location: `${pathSelected} Assessment`,
                    action: '',
                    field: '',
                  });
                }}
              >
                {useTranslation('start_over')}
              </Link>
            </div>
          </div>
          <div className="bx--row">
            <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 progressBar">
              <Progress step={stepcompleted} />
            </div>
          </div>
          <div className="bx--row"> {renderComponent()}</div>
        </ModalBody>
      </Modal>
    </>
  );
};
